import axiosInstance, { getApiErrorMessage } from "../auth/apiConfig";
import { rtfToPlainText } from "../utils/rtfToPlainText";

const WEB_BASE = "/api/v1/web/discharge-summaries";

const mapLookupItem = (item, { truncateLabel = false } = {}) => {
  const plain = rtfToPlainText(item?.name || "");
  let label = plain;
  if (truncateLabel && plain.length > 80) {
    label = `${plain.slice(0, 80).trimEnd()}…`;
  }
  return {
    id: item?.id,
    value: item?.id,
    label: label || `Item ${item?.id}`,
    description: plain,
    genericName: item?.extra || "",
    data: item,
  };
};

export const toApiDate = (dateValue) => {
  if (!dateValue) return null;
  const datePart = String(dateValue).trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(datePart) ? datePart : null;
};

/** Normalize time to HH:mm:ss for API LocalTime. */
export const toApiTime = (timeValue) => {
  if (!timeValue) return null;
  const raw = String(timeValue).trim();
  if (!raw) return null;
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [h, m, s = "00"] = raw.split(":");
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  const match = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    if (period === "PM" && hours < 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;
    return `${String(hours).padStart(2, "0")}:${minutes}:00`;
  }
  return null;
};

export const formatDisplayTime = (timeValue) => {
  if (!timeValue) return "";
  const raw = String(timeValue);
  const timePart = raw.includes("T") ? raw.split("T")[1] : raw;
  return timePart.slice(0, 5);
};

const dropdownValue = (option) => {
  if (option == null) return null;
  if (typeof option === "object") return option.value ?? option.id ?? null;
  return option;
};

const dropdownLabel = (option) => {
  if (option == null) return "";
  if (typeof option === "object") return option.label ?? option.value ?? "";
  return String(option);
};

const serializeTreatmentRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  return rows
    .filter((r) => Object.values(r).some((v) => String(v || "").trim()))
    .map((r) =>
      [r.drug, r.dose, r.duration, r.durationIn, r.qty, r.instruction]
        .map((v) => String(v || "").trim())
        .join(" | ")
    )
    .join("\n");
};

const serializeInvestigationRows = (rows, baseText) => {
  const header = String(baseText || "").trim();
  if (!Array.isArray(rows) || rows.length === 0) return header;
  const lines = rows
    .filter((r) => Object.values(r).some((v) => String(v || "").trim() && v !== r.id && v !== r.srNo))
    .map((r) =>
      [r.srNo, r.testDate, r.testName, r.parameter, r.result, r.normalRange]
        .map((v) => String(v || "").trim())
        .join(" | ")
    );
  if (!lines.length) return header;
  return [header, ...lines].filter(Boolean).join("\n");
};

export const listDischargeSummaryTemplates = async () => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/templates`);
    return response.data?.data ?? [];
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to load templates"));
  }
};

export const listFollowUpInstructions = async () => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/follow-up-instructions`);
    return (response.data?.data ?? []).map((item) =>
      mapLookupItem(item, { truncateLabel: true })
    );
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load follow-up instructions")
    );
  }
};

export const searchDrugs = async (query, top = 50) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/drugs`, {
      params: {
        q: query?.trim() || undefined,
        top,
      },
    });
    return (response.data?.data ?? []).map((item) => mapLookupItem(item));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to search drugs"));
  }
};

export const searchDoses = async (query, top = 50) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/doses`, {
      params: {
        q: query?.trim() || undefined,
        top,
      },
    });
    return (response.data?.data ?? []).map((item) => mapLookupItem(item));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, "Failed to search doses"));
  }
};

export const getDischargeSummaryByAdmission = async (admissionId) => {
  try {
    const response = await axiosInstance.get(WEB_BASE, {
      params: { admissionId },
    });
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load discharge summary")
    );
  }
};

export const getDischargeSummaryById = async (dischargeSummaryId) => {
  try {
    const response = await axiosInstance.get(`${WEB_BASE}/${dischargeSummaryId}`);
    return response.data?.data ?? null;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to load discharge summary")
    );
  }
};

export const createDischargeSummary = async (payload) => {
  try {
    const response = await axiosInstance.post(WEB_BASE, payload);
    return response.data?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to save discharge summary")
    );
  }
};

export const updateDischargeSummary = async (dischargeSummaryId, payload) => {
  try {
    const response = await axiosInstance.put(
      `${WEB_BASE}/${dischargeSummaryId}`,
      payload
    );
    return response.data?.data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, "Failed to update discharge summary")
    );
  }
};

/** Map UI form + patient → SaveDischargeSummaryRequest. */
export const mapFormToSavePayload = (formData, patient, userId) => {
  const pulse = Number(formData.pulse);
  const respiration = Number(formData.resp ?? formData.respiration);
  const weight = Number(formData.weight);
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const nowTime = `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes()
  ).padStart(2, "0")}:00`;

  const treatmentGivenText = [
    String(formData.treatmentGivenText || "").trim(),
    serializeTreatmentRows(formData.treatmentGivenRows),
  ]
    .filter(Boolean)
    .join("\n");

  const treatmentAdvisedText = [
    String(formData.treatmentAdvisedText || "").trim(),
    serializeTreatmentRows(formData.treatmentAdvisedRows),
  ]
    .filter(Boolean)
    .join("\n");

  return {
    admissionId: Number(patient?.admissionId),
    dischargeId: Number(patient?.dischargeId || 0),
    history: formData.history || "",
    diagnosis: formData.finalDiagnosis || formData.diagnosis || "",
    investigation: serializeInvestigationRows(
      formData.investigationRows,
      formData.investigation
    ),
    clinicalFinding: formData.clinicalFinding || "",
    opertiveNotes: formData.operativeNotes || formData.opertiveNotes || "",
    treatmentGiven: treatmentGivenText,
    treatmentAdvisedAfterDischarge: treatmentAdvisedText,
    followUpDate: toApiDate(formData.followUpDate),
    remark: formData.followUpInstructions || formData.remark || "",
    dischargeSummaryDate: toApiDate(formData.summaryDate) || today,
    opDate: toApiDate(formData.operativeDate || formData.opDate),
    opTime: toApiTime(formData.operativeTime || formData.opTime),
    dischargeDoctor1: Number(dropdownValue(formData.doctor1 || formData.dischargeDoctor1) || 0),
    dischargeDoctor2: Number(dropdownValue(formData.doctor2 || formData.dischargeDoctor2) || 0),
    dischargeDoctor3: Number(dropdownValue(formData.doctor3 || formData.dischargeDoctor3) || 0),
    dischargeSummaryTime: toApiTime(formData.summaryTime) || nowTime,
    doctorAssistantName: formData.doctorAssistant || formData.doctorAssistantName || "",
    conditionOnDischarge: formData.conditionOnDischarge || "",
    doctorNotes: formData.doctorNotes || "",
    investigationOther: formData.otherInvestigation || "",
    userId: Number(userId || 1),
    bp: formData.bp != null ? String(formData.bp) : "",
    pulse: Number.isFinite(pulse) ? pulse : 0,
    respiration: Number.isFinite(respiration) ? respiration : 0,
    spo2: formData.spo2 != null && formData.spo2 !== "" ? String(formData.spo2) : "",
    operativeProc: formData.operativeProc || "",
    surgeonName: dropdownLabel(formData.surgeonName),
    asstSurgeonName: dropdownLabel(formData.asstSurgeonName),
    anaestheticsName: dropdownLabel(formData.anaestheticsName),
    anaesthesiaType: dropdownLabel(formData.anaesthesiaType),
    emergencyContact: formData.emergencyContact || "",
    dietAdvice: formData.dietAdvice || "",
    physiotherapy: formData.physiotherapy || "",
    whenToContact: dropdownLabel(formData.whenToContact),
    howToContact: dropdownLabel(formData.howToContact),
    allergyHistory: formData.allergy || "",
    reactionHistory: formData.reaction || "",
    implantDetails: formData.implantDetails || "",
    instrumentDetails: formData.instrumentDetails || "",
    weight: Number.isFinite(weight) ? weight : 0,
    otDiagnosis: "",
    procedureIndication: formData.procedureIndication || "",
  };
};

/** Map API summary → react-hook-form patch values. */
export const mapSummaryToFormValues = (summary, doctorOptions = []) => {
  if (!summary) return {};
  const findDoctor = (id) => {
    if (!id) return null;
    return (
      doctorOptions.find((o) => Number(o.id) === Number(id)) || {
        id,
        value: id,
        label: String(id),
      }
    );
  };
  return {
    finalDiagnosis: summary.diagnosis || "",
    clinicalFinding: summary.clinicalFinding || "",
    bp: summary.bp || "",
    pulse: summary.pulse != null ? String(summary.pulse) : "",
    resp: summary.respiration != null ? String(summary.respiration) : "",
    spo2: summary.spo2 || "",
    history: summary.history || "",
    allergy: summary.allergyHistory || "",
    reaction: summary.reactionHistory || "",
    investigation: summary.investigation || "",
    otherInvestigation: summary.investigationOther || "",
    operativeNotes: summary.opertiveNotes || "",
    operativeDate: summary.opDate || "",
    operativeTime: formatDisplayTime(summary.opTime),
    implantDetails: summary.implantDetails || "",
    instrumentDetails: summary.instrumentDetails || "",
    procedureIndication: summary.procedureIndication || "",
    operativeProc: summary.operativeProc || "",
    surgeonName: summary.surgeonName
      ? { id: summary.surgeonName, value: summary.surgeonName, label: summary.surgeonName }
      : null,
    asstSurgeonName: summary.asstSurgeonName
      ? {
          id: summary.asstSurgeonName,
          value: summary.asstSurgeonName,
          label: summary.asstSurgeonName,
        }
      : null,
    anaestheticsName: summary.anaestheticsName
      ? {
          id: summary.anaestheticsName,
          value: summary.anaestheticsName,
          label: summary.anaestheticsName,
        }
      : null,
    anaesthesiaType: summary.anaesthesiaType
      ? {
          id: summary.anaesthesiaType,
          value: summary.anaesthesiaType,
          label: summary.anaesthesiaType,
        }
      : null,
    treatmentGivenText: summary.treatmentGiven || "",
    treatmentAdvisedText: summary.treatmentAdvisedAfterDischarge || "",
    followUpDate: summary.followUpDate || "",
    followUpInstructions: rtfToPlainText(summary.remark || ""),
    conditionOnDischarge: rtfToPlainText(summary.conditionOnDischarge || ""),
    doctorNotes: rtfToPlainText(summary.doctorNotes || ""),
    doctor1: findDoctor(summary.dischargeDoctor1),
    doctor2: findDoctor(summary.dischargeDoctor2),
    doctor3: findDoctor(summary.dischargeDoctor3),
    doctorAssistant: summary.doctorAssistantName || "",
    weight: summary.weight != null ? String(summary.weight) : "0",
    emergencyContact: summary.emergencyContact || "",
    whenToContact: summary.whenToContact
      ? {
          id: summary.whenToContact,
          value: summary.whenToContact,
          label: summary.whenToContact,
        }
      : null,
    howToContact: summary.howToContact
      ? {
          id: summary.howToContact,
          value: summary.howToContact,
          label: summary.howToContact,
        }
      : null,
    dietAdvice: summary.dietAdvice || "",
    physiotherapy: summary.physiotherapy || "",
  };
};

export const mapTemplateToDropdownOption = (t) => ({
  id: t.dischargeTempId,
  value: t.dischargeTempId,
  label: t.dischargeTempName || `Template ${t.dischargeTempId}`,
  data: {
    finalDiagnosis: t.finalDiagnosis || "",
    history: t.history || "",
    clinicalFinding: t.clinicalFinding || "",
    investigation: t.investigation || "",
    operativeNotes: t.operativeNotes || "",
    treatmentGivenText: t.treatmentGiven || "",
    treatmentAdvisedText: t.treatmentAdvisedAfterDischarge || "",
    followUpInstructions: rtfToPlainText(t.followupInstruction || ""),
  },
});
